import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket, useNotifications, useNotificationsAPI, enrichLegacyNotification } from '../hooks/useWebSocket';
import { getDisplayTitle, getDisplayMessage, getDisplayContent, truncateForBrowser, formatRelativeTime } from '../lib/notification-display';
import { NotificationDetail } from './NotificationDetail';

/**
* Notification shape used within the bell component.
* Compatible with both the full Notification type and the NotificationPayload
* returned by useNotificationsAPI / useNotifications hooks.
*/
interface BellNotification {
id: string;
type?: string;
notificationType?: string;
title: string;
message: string;
shortformTitle?: string;
shortformMessage?: string;
relatedHoldRequestId?: string;
relatedLineItemId?: string;
isRead?: boolean;
createdAt: string;
}

interface NotificationBellProps {
token: string | null;
}

/**
* Notification bell component with real-time updates and API integration
*/
export function NotificationBell({ token }: NotificationBellProps) {
const navigate = useNavigate();
const { isConnected, unreadCount } = useWebSocket(token);
const { 
notifications: apiNotifications, 
loading, 
fetchNotifications, 
markAsRead: markAsReadAPI, 
markAllAsRead: markAllAsReadAPI,
setNotifications: setApiNotifications 
} = useNotificationsAPI(token);

const [showDropdown, setShowDropdown] = useState(false);
const [selectedNotification, setSelectedNotification] = useState<BellNotification | null>(null);
const [viewedNotificationIds, setViewedNotificationIds] = useState<Set<string>>(new Set());
const containerRef = useRef<HTMLDivElement>(null);

// Close dropdown when clicking outside the bell container
useEffect(() => {
if (!showDropdown) return;

const handleClickOutside = (event: MouseEvent) => {
if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
setShowDropdown(false);
setSelectedNotification(null);
}
};

document.addEventListener('mousedown', handleClickOutside);
return () => document.removeEventListener('mousedown', handleClickOutside);
}, [showDropdown]);

// Fetch existing notifications when component mounts or token changes
useEffect(() => {
fetchNotifications(20, 0, true); // Fetch unread notifications
}, [fetchNotifications]);

// Listen for new notifications via WebSocket
useNotifications(
useCallback((notification) => {
// Enrich legacy notifications (missing shortform fields) with generated shortform
// so that display is consistent between WebSocket and REST API paths.
// Validates: Requirements 5.1, 5.2, 5.4
const enriched = enrichLegacyNotification(notification);

// Add to the beginning of the notifications list (deduplicate by ID)
setApiNotifications((prev) => {
// Check if notification already exists
const exists = prev.some(n => n.id === enriched.id);
if (exists) {
return prev;
}
return [enriched, ...prev];
});

// Show browser notification if supported
// Uses getDisplayContent() to handle both new and legacy notifications
// with on-the-fly shortform generation (greeting removal, URL removal, truncation).
// Truncates body to 100 characters per Requirement 11.3/11.4
// On click, navigates to the related resource per Requirement 11.5
// Handles unsupported browser and permission denied gracefully
// Validates: Requirements 5.1, 5.2, 5.4, 11.1, 11.2, 11.3, 11.4, 11.5
const displayContent = getDisplayContent(enriched);
if ('Notification' in window && Notification.permission === 'granted') {
try {
const browserNotification = new Notification(displayContent.title, {
body: truncateForBrowser(displayContent.message, 100),
icon: '/notification-icon.png',
});

// Navigate to the related resource when the browser notification is clicked
// Requirement 11.5: clicking a browser notification navigates to the related resource
browserNotification.onclick = () => {
window.focus();
if (enriched.relatedLineItemId) {
navigate(`/items/${enriched.relatedLineItemId}`);
} else if (enriched.relatedHoldRequestId) {
navigate(`/holds/${enriched.relatedHoldRequestId}`);
}
browserNotification.close();
};
} catch (error) {
// Fail silently — in-app notification in the bell still works
console.warn('Browser notification failed:', error);
}
}
}, [setApiNotifications, navigate])
);

const handleMarkAsRead = async (notificationId: string) => {
await markAsReadAPI(notificationId);
};

const handleMarkAllAsRead = async () => {
await markAllAsReadAPI();
};

const handleNotificationClick = async (notification: BellNotification) => {
// Mark as read when clicked
if (!notification.isRead) {
await markAsReadAPI(notification.id);
}

// Navigate to the most specific related resource
if (notification.relatedLineItemId) {
setShowDropdown(false);
try {
navigate(`/items/${notification.relatedLineItemId}`);
} catch (error) {
console.error('Navigation error:', error);
window.location.href = `/items/${notification.relatedLineItemId}`;
}
} else if (notification.relatedHoldRequestId) {
setShowDropdown(false);
try {
navigate(`/holds/${notification.relatedHoldRequestId}`);
} catch (error) {
console.error('Navigation error:', error);
window.location.href = `/holds/${notification.relatedHoldRequestId}`;
}
} else {
// No related resource — show detail view
showNotificationDetail(notification);
}
};

/**
* Show the detail view for a notification and track it as viewed.
* Requirements: 4.1, 4.5
*/
const showNotificationDetail = (notification: BellNotification) => {
setSelectedNotification(notification);
setViewedNotificationIds((prev) => {
const next = new Set(prev);
next.add(notification.id);
return next;
});
// Mark as read when viewing detail
if (!notification.isRead) {
markAsReadAPI(notification.id);
}
};

/**
* Return from detail view to the notification list.
* Requirement: 4.4
*/
const handleDetailBack = () => {
setSelectedNotification(null);
};

const handleDropdownToggle = () => {
setShowDropdown(!showDropdown);

// Reset detail view when toggling dropdown
if (showDropdown) {
setSelectedNotification(null);
}

// Refresh notifications when opening dropdown
if (!showDropdown && token) {
fetchNotifications(20, 0, true);
}
};

return (
<div className="relative" ref={containerRef}>
{/* Connection status indicator */}
<div className="absolute -top-1 -right-1">
<div
className={`w-3 h-3 rounded-full ${
isConnected ? 'bg-green-500' : 'bg-red-500'
}`}
title={isConnected ? 'Connected' : 'Disconnected'}
/>
</div>

{/* Notification bell button */}
<button
onClick={handleDropdownToggle}
className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
aria-label="Notifications"
>
<svg
className="w-6 h-6 text-white"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
/>
</svg>

{/* Unread count badge */}
{unreadCount > 0 && (
<span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
{unreadCount > 99 ? '99+' : unreadCount}
</span>
)}
</button>

{/* Notification dropdown */}
{showDropdown && (
<div className="absolute right-0 mt-2 w-80 bg-white rounded-lg border border-neutral-200 z-50">
<div className="px-2.5 py-1.5 border-b border-neutral-200 flex justify-between items-center">
<h3 className="text-xs font-semibold text-neutral-900">
Notifications
</h3>
{apiNotifications.length > 0 && (
<button
onClick={handleMarkAllAsRead}
className="text-sm text-outfront-purple hover:text-outfront-purple"
disabled={loading}
>
Mark all as read
</button>
)}
</div>

<div className="max-h-96 overflow-y-auto">
{/* Detail view: show when a notification is selected */}
{selectedNotification ? (
<NotificationDetail
notification={selectedNotification}
onBack={handleDetailBack}
/>
) : loading ? (
<div className="p-8 text-center text-neutral-600">
<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-outfront-purple mx-auto mb-2"></div>
<p>Loading notifications...</p>
</div>
) : apiNotifications.length === 0 ? (
<div className="p-8 text-center text-neutral-600">
<svg
className="w-12 h-12 mx-auto mb-2 text-neutral-400"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
/>
</svg>
<p>No new notifications</p>
</div>
) : (
<ul className="divide-y divide-neutral-100">
{apiNotifications.map((notification) => {
const isViewed = viewedNotificationIds.has(notification.id);
// Use getDisplayTitle() and getDisplayMessage() for shortform display.
// These prefer shortform fields when available and non-empty,
// falling back to longform content for legacy notifications.
// Validates: Requirements 2.1, 2.2, 5.2
const displayTitle = getDisplayTitle(notification);
const displayMessage = getDisplayMessage(notification);
return (
<li
key={notification.id}
className={`px-2.5 py-1.5 transition-colors hover:bg-neutral-50 ${
!notification.isRead ? 'bg-outfront-purple-light border-l-2 border-l-outfront-purple' : ''
} ${isViewed ? 'opacity-75' : ''}`}
data-viewed={isViewed ? 'true' : undefined}
data-unread={!notification.isRead ? 'true' : undefined}
>
<div className="flex justify-between items-start gap-1">
<div 
className="flex-1 min-w-0 cursor-pointer"
onClick={() => handleNotificationClick(notification)}
>
<div className="flex items-center gap-1.5">
{!notification.isRead && (
<span className="w-1.5 h-1.5 rounded-full bg-outfront-purple flex-shrink-0" data-testid="unread-indicator" />
)}
<h4 className={`text-xs text-neutral-900 truncate ${!notification.isRead ? 'font-bold' : 'font-medium'}`}>
{displayTitle}
</h4>
{isViewed && (
<span
className="text-[10px] text-neutral-400 flex-shrink-0"
title="Viewed"
aria-label="Viewed"
data-testid={`notification-viewed-indicator-${notification.id}`}
>
✓
</span>
)}
</div>
<p className="text-[11px] text-neutral-600 mt-0.5 line-clamp-1">
{displayMessage}
</p>
<p className="text-[10px] text-neutral-400 mt-0.5 leading-tight">
{formatRelativeTime(notification.createdAt)}
</p>
</div>
<div className="flex flex-col items-center gap-0.5 flex-shrink-0 ml-1">
{/* Info/expand button to view full details (Requirement 4.1) */}
<button
onClick={(e) => {
e.stopPropagation();
showNotificationDetail(notification);
}}
className="text-neutral-400 hover:text-outfront-purple transition-colors"
title="View details"
aria-label="View notification details"
data-testid={`notification-detail-btn-${notification.id}`}
>
<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
</svg>
</button>
<button
onClick={(e) => {
e.stopPropagation();
handleMarkAsRead(notification.id);
}}
className="text-outfront-purple hover:text-outfront-purple text-xs"
disabled={loading}
title="Mark as read"
>
✓
</button>
</div>
</div>
</li>
);
})}
</ul>
)}
</div>

{/* Footer with connection status and view all link */}
<div className="p-2 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between">
<span
className={`inline-flex items-center text-xs ${
isConnected ? 'text-green-600' : 'text-red-600'
}`}
>
<span
className={`w-2 h-2 rounded-full mr-2 ${
isConnected ? 'bg-green-500' : 'bg-red-500'
}`}
/>
{isConnected ? 'Real-time updates active' : 'Reconnecting...'}
</span>
<button
onClick={() => {
setShowDropdown(false);
navigate('/notifications');
}}
className="text-xs text-outfront-purple hover:text-outfront-purple font-medium"
>
View all
</button>
</div>
</div>
)}
</div>
);
}
